import React from "react";
import StatusBadge from "./StatusBadge";
import ActionButtons from "./ActionButtons";
import UserInfoCell from "./UserInfoCell";
import PaymentMethodCell from "../DepositRequests/PaymentMethodCell";
import { buildUrl } from "helper/buildUrl";
import { FileUploadWithLimit } from "./FileUploadWithLimit";
import { API_URL } from "helper/Constants";

const WithdrawalRequestsTable = ({
  requests,
  onAllow,
  onApprove,
  onReject,
  screenshot,
  onScreenshotChange,
    screenshotMap,          // CHANGE: a map of { [id]: File }
  section = "pending",
}) => {
  if (requests.length === 0) {
    return (
      <p className="text-slate-500 text-center py-8 bg-slate-50 rounded-lg">
        No {section} withdrawal requests.
      </p>
    );
  }

  const hasRejectionReasons = requests.some((req) => req.rejectionReason);
  const showActions = section === "pending" || section === "allowed";
  const showScreenshotUpload = section === "allowed";
  const showProofHeader =
    section === "processed" && requests.some((req) => !!req.screenshotUrl);

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm">
      <table className="min-w-full  divide-y divide-slate-700">
        <thead className="bg-slate-700">
          <tr className="">
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">User</th>
            <th className="border px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
            <th className="border px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Method</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Account Details</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Reference</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
            {showScreenshotUpload && (
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Payment Proof</th>
            )}
            {showProofHeader && (
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Payment Proof</th>
            )}
            {showActions && (
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
            )}
            {hasRejectionReasons && (
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Reason</th>
            )}
          </tr>
        </thead>
        <tbody>
          {requests.map((request, index) => (
            <WithdrawalRequestRow
              key={request._id || request.id}
              request={request}
              onAllow={onAllow}
              onApprove={onApprove}
              onReject={onReject}
              screenshot={screenshot}
              screenshotMap={screenshotMap}     
              onScreenshotChange={onScreenshotChange}
              section={section}
              showActions={showActions}
              showRejectionReason={hasRejectionReasons}
              isEven={index % 2 === 0}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const WithdrawalRequestRow = ({
  request,
  onAllow,
  onApprove,
  onReject,
  screenshotMap,
  onScreenshotChange,
  section,
  showActions,
  showRejectionReason,
  isEven,
}) => {
  const rowId = request._id || request.id; // normalize id once
  const showScreenshotUpload = section === "allowed";
  const showScreenshotView = section === "processed" && !!request.screenshotUrl;
const fileForRow = screenshotMap?.[rowId];
  return (
    <tr
      className={`border border-slate-200 ${
        isEven ? "bg-white" : "bg-slate-50"
      } hover:bg-slate-100 transition-colors`}
    >
      <UserInfoCell
        userId={request.userId || request.user?._id}
        username={request.username || request.user?.username}
      />

      <td className="border border-slate-200 px-4 py-3 font-medium">
        ${Number(request.amount || 0).toFixed(2)}
      </td>

      <PaymentMethodCell
        method={ request.paymentMethod}
      />

      <td className="border border-slate-200 px-4 py-3 text-slate-600">
        {request.date ||
          new Date(request.createdAt || request.updatedAt || Date.now()).toLocaleDateString()}
      </td>
      <td className="border border-slate-200 px-4 py-3 text-slate-600">
        {request.accountDetails}
      </td>
      <td className="border border-slate-200 px-4 py-3 font-mono text-sm text-slate-700 bg-slate-100 rounded">
        {request.reference}
      </td>

      <td className="border border-slate-200 px-4 py-3">
        <StatusBadge status={request.status} />
      </td>

      {/* Screenshot Upload for Allowed */}
{showScreenshotUpload && (
  <td className="border border-slate-200 px-4 py-3">
    <div className="flex flex-col space-y-2">
      <FileUploadWithLimit
        rowId={rowId}
        fileForRow={fileForRow}
        onScreenshotChange={onScreenshotChange}
      />
    </div>
  </td>
)}


      {/* Screenshot View for Processed */}
      {showScreenshotView && (
        <td className="border border-slate-200 px-4 py-3">
              <a
                href={buildUrl(API_URL, request.screenshot.path)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-400 hover:underline"
              >
                {/* Image preview */}
                <img
                  src={buildUrl(API_URL, request.screenshot.path)}
                  alt="Deposit Proof"
                  width="100"
                  height="100"
                  className="object-cover rounded-md"
                />
              </a>
        </td>
      )}

      {!showScreenshotUpload && !showScreenshotView && section === "processed" && (
        <td className="border border-slate-200 px-4 py-3">
          <span className="text-slate-400 text-sm">No proof</span>
        </td>
      )}

     {showActions && (
        <td className="border border-slate-200 px-4 py-3">
          <ActionButtons
            onApprove={() => onApprove(rowId)}
            onReject={() => onReject(request)}
            onAllow={() => onAllow(rowId)}
            showAllow={section === "pending"}
            showApprove={section === "allowed"}
          />
        </td>
      )}
      {showRejectionReason && request.rejectionReason && (
        <td className="border border-slate-200 px-4 py-3 text-sm text-red-600 bg-red-50">
          {request.rejectionReason}
        </td>
      )}
    </tr>
  );
};

export default WithdrawalRequestsTable;
